import type { Content, KeyTextField, RichTextField } from '@prismicio/client';
import { formatString } from '@/i18n/catalogue';

/**
 * The marking rules for activity cards, as pure functions over the slice and
 * whatever the learner did to it.
 *
 * Everything the reducer, the card and the footer need to agree on lives here:
 * where a region is, whether a pin landed in it, whether every marker sits in
 * a target zone, whether a tapped sequence matches. The card renders, the
 * footer marks, and both read the same geometry — which is why a hotspot can
 * be tested without a DOM.
 *
 * Coordinates are percentages of the image, so nothing here depends on how
 * big the card is drawn.
 */

/** The four variations, as the slice types the renderers switch on. */
export type HotspotSlice = Extract<Content.ActivitySlice, { variation: 'hotspot' }>;
export type DragTargetsSlice = Extract<Content.ActivitySlice, { variation: 'drag_targets' }>;
export type OrderingSlice = Extract<Content.ActivitySlice, { variation: 'ordering' }>;
export type FreeTextSlice = Extract<Content.ActivitySlice, { variation: 'free_text' }>;
/** Every variation that plays as an interactive card — free text lives in the footer. */
export type ActivityCardSlice = Exclude<Content.ActivitySlice, FreeTextSlice>;

/** A point on the image, in percent from its top-left corner. */
export type Point = { x: number; y: number };

/** A percentage box over the image: a hotspot region or a drag target zone. */
export type Zone = {
  /** Authored label — the words a receipt bubble is built from. */
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  correct: boolean;
};

/** Prismic Number fields are `number | null`; a missing edge is a zero-size box. */
const zoneFrom = (
  item: { label: KeyTextField; x: number | null; y: number | null; w: number | null; h: number | null },
  correct: boolean,
): Zone => ({
  label: item.label?.trim() ?? '',
  x: item.x ?? 0,
  y: item.y ?? 0,
  w: item.w ?? 0,
  h: item.h ?? 0,
  correct,
});

export function hotspotZones(slice: HotspotSlice): Array<Zone> {
  return slice.primary.regions.map((region) => zoneFrom(region, region.is_correct === true));
}

/**
 * Target zones are correct unless an editor marks one wrong, like the rip. A
 * zone saved before `is_correct` existed counts as correct.
 */
export function targetZones(slice: DragTargetsSlice): Array<Zone> {
  return slice.primary.targets.map((target) => zoneFrom(target, target.is_correct !== false));
}

/** How many markers the learner has to place. At least one, whatever was authored. */
export function markerCount(slice: DragTargetsSlice): number {
  return Math.max(1, slice.primary.marker_count ?? 1);
}

/** Inclusive on both edges — a pin exactly on a boundary is inside. */
export function contains(zone: Zone, point: Point): boolean {
  return point.x >= zone.x && point.x <= zone.x + zone.w && point.y >= zone.y && point.y <= zone.y + zone.h;
}

/** The zone a point landed in. Later zones win, so a small zone can sit over a big one. */
export function zoneAt(zones: Array<Zone>, point: Point): Zone | null {
  return zones.filter((zone) => contains(zone, point)).at(-1) ?? null;
}

/** The centre of a zone — where the keyboard fallback puts the pin. */
export function centreOf(zone: Zone): Point {
  return { x: zone.x + zone.w / 2, y: zone.y + zone.h / 2 };
}

/** One step of an ordering activity. Ids are positional, so the answer is `step-0, step-1, …`. */
export type OrderStep = { id: string; label: string };

/** The steps in their authored — that is, correct — order. */
export function orderSteps(slice: OrderingSlice): Array<OrderStep> {
  return slice.primary.steps.map((item, index) => ({ id: `step-${index}`, label: item.step?.trim() ?? '' }));
}

/** A tiny seeded PRNG (mulberry32), so a shuffle is stable across SSR and hydration. */
function seededRandom(seed: string): () => number {
  let state = 0;

  for (const character of seed) {
    state = (state * 31 + character.charCodeAt(0)) | 0;
  }

  return () => {
    state = (state + 0x6d2b79f5) | 0;

    let value = Math.imul(state ^ (state >>> 15), 1 | state);

    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;

    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The order the steps are *shown* in: shuffled, but deterministically from the
 * slice id. A `Math.random()` shuffle would render one order on the server and
 * another on the client, and re-shuffle the frozen card on every re-render.
 *
 * A shuffle that happens to come out correct is rotated, so the activity is
 * never already answered when it arms.
 */
export function shuffledSteps(slice: OrderingSlice): Array<OrderStep> {
  const steps = orderSteps(slice);

  if (steps.length < 2) {
    return steps;
  }

  const random = seededRandom(slice.primary.question_key?.trim() || slice.id);
  const shuffled = [...steps];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    const held = shuffled[index] as OrderStep;

    shuffled[index] = shuffled[swap] as OrderStep;
    shuffled[swap] = held;
  }

  const unchanged = shuffled.every((step, index) => step.id === steps[index]?.id);

  return unchanged ? [...shuffled.slice(1), shuffled[0] as OrderStep] : shuffled;
}

// --- Drafts ------------------------------------------------------------------

/**
 * The learner's in-progress answer, by variation. The card writes it, the
 * footer marks it, and a frozen card re-renders from the submitted one.
 */
export type ActivityDraft =
  | { kind: 'hotspot'; point: Point | null }
  /** One slot per marker; `null` while a marker is still in the tray. */
  | { kind: 'drag'; points: Array<Point | null> }
  | { kind: 'ordering'; sequence: Array<string> };

/** An empty draft for a slice — what arming, and Reset, start from. */
export function emptyDraft(slice: ActivityCardSlice): ActivityDraft {
  switch (slice.variation) {
    case 'hotspot':
      return { kind: 'hotspot', point: null };

    case 'drag_targets':
      return { kind: 'drag', points: Array.from({ length: markerCount(slice) }, () => null) };

    case 'ordering':
      return { kind: 'ordering', sequence: [] };
  }
}

/** True once there is enough of an answer to check. */
export function isDraftComplete(slice: ActivityCardSlice, draft: ActivityDraft): boolean {
  if (slice.variation === 'hotspot' && draft.kind === 'hotspot') {
    return draft.point !== null;
  }

  if (slice.variation === 'drag_targets' && draft.kind === 'drag') {
    return draft.points.every((point) => point !== null);
  }

  if (slice.variation === 'ordering' && draft.kind === 'ordering') {
    return draft.sequence.length === slice.primary.steps.length;
  }

  return false;
}

// --- Marking -----------------------------------------------------------------

/**
 * The verdict on one Check. `mistake` is the index of the region, zone or step
 * a wrong check blamed, or `null` when nothing authored is to blame. Saved runs
 * store the index, so it replays into the same item of a translated slice.
 */
export type ActivityVerdict = { correct: boolean; labels: Array<string>; mistake: number | null };

export function markHotspot(slice: HotspotSlice, point: Point): ActivityVerdict {
  const zones = hotspotZones(slice);
  const zone = zoneAt(zones, point);
  const correct = zone?.correct === true;

  return {
    correct,
    labels: zone === null ? [] : [zone.label],
    mistake: zone === null || correct ? null : zones.lastIndexOf(zone),
  };
}

/** Correct when every marker has been placed and each one sits inside a correct zone. */
export function markDrag(slice: DragTargetsSlice, points: Array<Point | null>): ActivityVerdict {
  const zones = targetZones(slice);
  const landed = points.map((point) => (point === null ? null : zoneAt(zones, point)));
  const correct = landed.length === markerCount(slice) && landed.every((zone) => zone?.correct === true);
  // The same zone twice is one label: "in the safe zone", not "in the safe zone, in the safe zone".
  const labels = [...new Set(landed.filter((zone) => zone !== null).map((zone) => zone.label))];
  const wrongZone = landed.find((zone) => zone !== null && !zone.correct);

  return { correct, labels, mistake: correct || !wrongZone ? null : zones.lastIndexOf(wrongZone) };
}

/**
 * Correct when the tapped sequence is the authored order, start to finish. A
 * wrong one blames the first step out of place.
 */
export function markOrdering(slice: OrderingSlice, sequence: Array<string>): ActivityVerdict {
  const steps = orderSteps(slice);
  const correct = sequence.length === steps.length && sequence.every((id, index) => id === steps[index]?.id);
  const labels = sequence.map((id) => steps.find((step) => step.id === id)?.label ?? '');
  const firstOut = steps.findIndex((step, index) => sequence[index] !== step.id);

  return { correct, labels, mistake: correct || firstOut < 0 ? null : firstOut };
}

/** Marks whatever the learner drafted, against whatever the slice is. */
export function markDraft(slice: ActivityCardSlice, draft: ActivityDraft): ActivityVerdict {
  if (slice.variation === 'hotspot' && draft.kind === 'hotspot' && draft.point !== null) {
    return markHotspot(slice, draft.point);
  }

  if (slice.variation === 'drag_targets' && draft.kind === 'drag') {
    return markDrag(slice, draft.points);
  }

  if (slice.variation === 'ordering' && draft.kind === 'ordering') {
    return markOrdering(slice, draft.sequence);
  }

  return { correct: false, labels: [], mistake: null };
}

/**
 * The feedback authored on the item a wrong check blamed, or `null` when there
 * is none. A slice saved before `feedback` existed has no value for it.
 */
export function mistakeFeedback(
  slice: Content.ActivitySlice,
  mistake: number | null | undefined,
): RichTextField | null {
  if (mistake === null || mistake === undefined) {
    return null;
  }

  const feedback = (() => {
    switch (slice.variation) {
      case 'hotspot':
        return slice.primary.regions[mistake]?.feedback;
      case 'drag_targets':
        return slice.primary.targets[mistake]?.feedback;
      case 'ordering':
        return slice.primary.steps[mistake]?.feedback;
      case 'free_text':
        return undefined;
    }
  })();

  return feedback && feedback.length > 0 ? feedback : null;
}

/**
 * The learner's receipt bubble: the slice's own template with the labels they
 * chose — "Tapped {label}" → "Tapped the dark channel". Never coordinates.
 *
 * `fallback` covers a tap that landed in no region at all, and an authored
 * template that is blank.
 */
export function activityReceipt(template: KeyTextField, labels: Array<string>, fallback: string): string {
  const chosen = labels.filter((label) => label !== '');
  const label = chosen.length > 0 ? chosen.join(', ') : fallback;
  const text = template?.trim();

  if (text === undefined || text === '') {
    return label;
  }

  return formatString(text, { label, labels: label, count: chosen.length });
}
